// src/labels/qrMaxLabel.js
// The 1.25" x 1" asset label (David chose option C, 10/28/2026): three lines
// turned sideways along the left edge, reading bottom to top (NEXT GEN CONST,
// the asset tag, the tool name), and the QR code on the right, 0.84" square
// (up from 0.48"). Every edge keeps 0.07-0.08" of white space: the first
// test print at 0.03" ran into the label's rounded corners. The QR code is an
// SVG (qrSvgFor) so the printer draws sharp squares, not a stretched picture.
// Each line shrinks to fit, and the tool name ends in "..." if still too long.
// The previous design is in ./archive/classicLabel.js.

import QRCode from 'qrcode';

// The QR code as one solid SVG shape: each run of dark squares in a row is a
// filled block, drawn a hair taller (1.02) so rows overlap. Printers and PDF
// viewers otherwise leave white hairlines where squares only just touch.
export function qrSvgFor(text) {
  const { size, data } = QRCode.create(text, { errorCorrectionLevel: 'M' }).modules;
  const q = 2;   // quiet zone, in squares (the text sits close on the left)
  let d = '';
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!data[r * size + c]) continue;
      let run = 1;
      while (c + run < size && data[r * size + c + run]) run++;
      d += `M${c + q} ${r + q}h${run}v1.02h-${run}z`;
      c += run - 1;
    }
  }
  const w = size + 2 * q;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${w}" shape-rendering="crispEdges">`
    + `<rect width="${w}" height="${w}" fill="#fff"/><path fill="#000" d="${d}"/></svg>`;
}

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export function qrMaxLabelHtml({ assetTag, toolName, qrSvg }) {
  const tag = escapeHtml(assetTag);
  const name = escapeHtml(toolName || '');
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>NGC Label — ${tag}</title>
<style>
  @page { size: 1.25in 1in; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; }

  /* ── The label, at real size ── */
  .label { width: 1.25in; height: 1in; background: #fff; color: #000; position: relative; overflow: hidden; }
  .qr { position: absolute; right: 0.07in; top: 0.08in; width: 0.84in; height: 0.84in; }
  .qr svg { width: 100%; height: 100%; display: block; }
  .strip { position: absolute; left: 0.07in; top: 0.08in; width: 0.24in; height: 0.84in;
    writing-mode: vertical-rl; transform: rotate(180deg);
    display: flex; flex-direction: column; justify-content: center; align-items: center; overflow: hidden; }
  .fit { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-height: 100%; line-height: 1.0; text-align: center; }
  .co { font-weight: 700; letter-spacing: 0.3pt; font-size: 4.5pt; }
  .tag { font-weight: 900; letter-spacing: -0.1pt; font-size: 8pt; }
  .name { font-weight: 400; font-size: 4.5pt; }

  /* ── PRINT OUTPUT: only the label ── */
  @media print {
    .instructions, .label-caption, .btn-print, .btn-close { display: none !important; }
    body { background: #fff; }
    .preview { width: auto; height: auto; }
    .scale { transform: none; }
  }

  /* ── SCREEN PREVIEW ── */
  @media screen {
    body { background: #f3f4f6; display: flex; flex-direction: column; align-items: center;
      justify-content: center; min-height: 100vh; padding: 20px; gap: 16px; }
    .instructions { background: white; border-radius: 10px; padding: 14px 16px; width: 100%; max-width: 360px;
      font-size: 12.5px; line-height: 1.6; color: #374151; box-shadow: 0 1px 4px rgba(0,0,0,0.1); }
    .instructions h2 { font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 10px; }
    .instructions ol { padding-left: 18px; }
    .instructions li { margin-bottom: 4px; }
    .instructions strong { color: #b91c1c; }
    .instructions .note { margin-top: 10px; font-size: 11px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 8px; }
    .label-wrapper { display: flex; flex-direction: column; align-items: center; gap: 10px; }
    .label-caption { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
    /* The real-size label, shown 2.5x larger */
    .preview { width: calc(1.25in * 2.5); height: calc(1in * 2.5); }
    .scale { transform: scale(2.5); transform-origin: 0 0; width: 1.25in; height: 1in; }
    .label { outline: 1px dashed #9ca3af; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .btn-print { background: #1A2C42; color: white; border: none; border-radius: 8px; padding: 12px 32px;
      font-size: 15px; font-weight: 700; cursor: pointer; width: 100%; max-width: 360px; }
    .btn-print:hover { background: #243d5a; }
    .btn-close { background: none; color: #6b7280; border: 1px solid #d1d5db; border-radius: 8px; padding: 8px 32px;
      font-size: 13px; cursor: pointer; width: 100%; max-width: 360px; }
    .btn-close:hover { background: #f9fafb; }
  }
</style>
</head>
<body>

<div class="instructions">
  <h2>Print Setup Checklist</h2>
  <ol>
    <li>In the print dialog, select <strong>Zebra ZD-411</strong></li>
    <li>Set <strong>Paper size</strong> to <strong>1.25" × 1"</strong><br>
        <span style="font-size:11px;color:#6b7280">(One-time: create this size in Windows printer preferences if missing)</span></li>
    <li>Set <strong>Scale</strong> to <strong>100%</strong> (not "Fit to page")</li>
    <li>Set <strong>Margins</strong> to <strong>None</strong></li>
  </ol>
  <div class="note">First time? Open Windows Settings → Printers → Zebra ZD-411 → Printing Preferences → Paper/Quality → create a custom paper size of 1.25" × 1".</div>
</div>

<div class="label-wrapper">
  <div class="label-caption">Label Preview (1.25" × 1")</div>
  <div class="preview"><div class="scale">
    <div class="label">
      <div class="strip">
        <div class="fit co" data-max="4.5" data-min="4">NEXT GEN CONST</div>
        <div class="fit tag" data-max="8" data-min="5.5">${tag}</div>
        ${name ? `<div class="fit name" data-max="4.5" data-min="4">${name}</div>` : ''}
      </div>
      <div class="qr">${qrSvg}</div>
    </div>
  </div></div>
</div>

<button class="btn-print" onclick="window.print()">🖨️ Print Label</button>
<button class="btn-close" onclick="window.close()">Close Window</button>

<script>
  // Shrink each line until it fits the label's height (data-max down to
  // data-min, in pt), then shrink all three together if they don't fit
  // side by side in the strip.
  (function () {
    var strip = document.querySelector('.strip');
    var lines = [].slice.call(strip.querySelectorAll('.fit'));
    lines.forEach(function (el) {
      for (var pt = parseFloat(el.dataset.max); pt >= parseFloat(el.dataset.min); pt -= 0.25) {
        el.style.fontSize = pt + 'pt';
        // 2px spare: sizes are rounded to whole pixels, and a line a hair
        // too long would otherwise end in "...".
        if (el.scrollHeight <= strip.clientHeight - 2) break;
      }
    });
    var across = function () { return lines.reduce(function (n, el) { return n + el.offsetWidth; }, 0); };
    for (var i = 0; i < 12 && across() > strip.clientWidth; i++) {
      lines.forEach(function (el) { el.style.fontSize = (parseFloat(el.style.fontSize) - 0.25) + 'pt'; });
    }
  })();
</script>
</body>
</html>`;
}
